/* eslint-disable max-lines */
import React, { Component, Dispatch, SetStateAction } from "react";

import localDB from "lib/IndexedDB/RessourcesDB";
import shaka from "shaka-player";

import client from "services/networking/client";
import { getFairPlayCertificate } from "services/networking/fairPlayCertificate";
import { DownloadSongMetadata } from "shared-types/video-metadata";

import { VideoContainer, Video } from "./VideoPlayer.style";
import {
	onLicenseResponse,
	getPlayerConfiguration,
	onLicenseRequest,
} from "./videoPlayer.utils";

export const VIDEO_PLAYER_TEST_ID = "video player";

// Tutorial for use of shaka player with pallycon can be found with the following links
// https://pallycon.com/docs/en/multidrm/clients/html5-player/

export interface VideoPlayerProps {
	setVideoElement: Dispatch<SetStateAction<HTMLVideoElement | null>>;
	controls?: boolean;
	licenseToken?: string;
	videoUrl?: string;
	onVideoLoadEnd: () => void;
	playerType?: string;
	waitingListDownloadMetadata: DownloadSongMetadata[];
	isStorageActivated: boolean;
	roomId: string | null;
}

interface VideoPlayerState {
	errorMessage: string;
	isFairPlayCertificateLoaded: boolean;
	isMounted: boolean;
}

class VideoPlayer extends Component<VideoPlayerProps, VideoPlayerState> {
	static defaultProps = {
		controls: true,
	};

	player: shaka.Player | null = null;
	storage: shaka.offline.Storage | null = null;
	state = {
		errorMessage: "",
		isFairPlayCertificateLoaded: false,
		isMounted: false,
	};

	videoRef = React.createRef<HTMLVideoElement>();

	componentDidMount() {
		this.setState({ isMounted: true }, async () => {
			// Install built-in polyfills to patch browser incompatibilities.
			shaka.polyfill.installAll();

			// Check to see if the browser supports the basic APIs Shaka needs.
			if (shaka.Player.isBrowserSupported()) {
				// Everything looks good!
				await this.initPlayer();
			} else {
				// This browser does not have the minimum set of APIs we need.
				console.error("Browser not supported!");
			}
		});
	}

	// eslint-disable-next-line complexity
	componentDidUpdate(prevprops: VideoPlayerProps, prevState: VideoPlayerState) {
		const { licenseToken, videoUrl } = this.props;
		const { isFairPlayCertificateLoaded } = this.state;
		if (
			isFairPlayCertificateLoaded !== prevState.isFairPlayCertificateLoaded ||
			licenseToken !== prevprops.licenseToken ||
			videoUrl !== prevprops.videoUrl
		) {
			videoUrl &&
				licenseToken &&
				isFairPlayCertificateLoaded &&
				this.playVideo();
		}
	}

	async playVideo() {
		const { licenseToken, videoUrl } = this.props;
		if (!this.player || !licenseToken || !videoUrl) {
			return;
		}

		this.player
			?.getNetworkingEngine()
			.registerRequestFilter(
				(type: shaka.net.NetworkingEngine.RequestType, request: any) => {
					if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
						onLicenseRequest(licenseToken, request);
					}
				},
			);

		// Load manifest and play the video
		this.player
			.load(videoUrl)
			.then(() => this.props.onVideoLoadEnd())
			.catch(this.onError);
	}

	async initPlayer() {
		const { isStorageActivated } = this.props;
		this.props.setVideoElement(this.videoRef.current);
		this.player = new shaka.Player(this.videoRef.current);

		// Add a filter on license responses
		this.player
			.getNetworkingEngine()
			.registerResponseFilter(
				(type: shaka.net.NetworkingEngine.RequestType, response: any) => {
					if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
						onLicenseResponse(response.data);
					}
				},
			);

		// Listen for error events.
		this.player.addEventListener("error", this.onErrorEvent);

		// Configure player with right DRM infos
		const fairPlayCertificate = isStorageActivated
			? await localDB.fetchOrGetData<string>(
					"/fairplay-certificate",
					(url: string) => client.get(url),
				)
			: await getFairPlayCertificate();
		if (!this.state.isMounted) return;
		this.setState({ isFairPlayCertificateLoaded: true });
		this.player.configure(
			getPlayerConfiguration(fairPlayCertificate, this.player),
		);
	}

	onErrorEvent(event: any) {
		// Extract the shaka.util.Error object from the event.
		this.onError(event.detail);
	}

	onError = (error: shaka.util.Error) => {
		// Log the error.
		console.error("Error code", error.code, "object", error);
		// Error codes can be found here https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
		switch (error.code) {
			case shaka.util.Error.Code.BAD_HTTP_STATUS:
				this.setState({
					errorMessage: `No video found at this adress: ${error.data[0]}`,
				});
				return;
		}
	};

	componentWillUnmount() {
		if (!this.player) {
			return;
		}
		this.props.setVideoElement(null);
		this.player.removeEventListener("error", this.onErrorEvent);
		this.player.destroy();
		this.setState({ isMounted: false });
	}

	render() {
		return (
			<VideoContainer>
				<Video
					ref={this.videoRef}
					// if controls are disabled, we want the video to take all screen
					width={this.props.controls ? "1280" : undefined}
					controls={this.props.controls}
					playsInline
					disablePictureInPicture
					data-testid={VIDEO_PLAYER_TEST_ID}
					data-playertype={this.props.playerType}
				/>
				{this.state.errorMessage && <div>{this.state.errorMessage}</div>}
			</VideoContainer>
		);
	}
}

export default VideoPlayer;
